import React from 'react';
import DiscordButton from './DiscordButton';

const Home = () => {
  const getCurrentDay = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  // Closed Wed May 13, 2026 through Mon May 18, 2026 for the
  // Golden State Pinball Festival. Auto-reverts on Tue May 19, 2026.
  const isClosedForGSPF = () => {
    const now = new Date();
    const start = new Date(2026, 4, 13, 0, 0, 0); // May 13, 2026 00:00 local
    const end = new Date(2026, 4, 19, 0, 0, 0);   // May 19, 2026 00:00 local
    return now >= start && now < end;
  };

  // Show advance-notice announcement starting Mon May 11, 2026
  // through the end of the closure (Tue May 19, 2026 00:00).
  const showGSPFAnnouncement = () => {
    const now = new Date();
    const start = new Date(2026, 4, 11, 0, 0, 0); // May 11, 2026 00:00 local
    const end = new Date(2026, 4, 19, 0, 0, 0);   // May 19, 2026 00:00 local
    return now >= start && now < end;
  };

  const isCurrentlyOpen = () => {
    if (isClosedForGSPF()) return false;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTime = currentHour + currentMinutes / 60;
    const dayOfWeek = now.getDay();

    // Special case: Sunday August 10th, 2025 opens at noon because Elsie forgot
    // If you inspect this, yes, Elsie forgot. Sorry Elsie!
    const isSpecialSunday =
      now.getFullYear() === 2025 &&
      now.getMonth() === 7 && // August is month 7 (0-indexed)
      now.getDate() === 10 &&
      dayOfWeek === 0;

    const hours = {
      0: { open: isSpecialSunday ? 12 : 11, close: 24 }, // Sunday: 11am - Midnight (except Aug 10, 2025: Noon)
      1: { open: 11, close: 21 }, // Monday: 11am - 9pm
      2: { open: 11, close: 21 }, // Tuesday: 11am - 9pm
      3: { open: 12, close: 23 }, // Wednesday: Noon - 11pm
      4: { open: 12, close: 24 }, // Thursday: Noon - Midnight
      5: { open: 11, close: 25 }, // Friday: 11am - 1am (next day)
      6: { open: 11, close: 25 }  // Saturday: 11am - 1am (next day)
    };

    const todayHours = hours[dayOfWeek];
    
    // Handle times that go past midnight
    if (todayHours.close > 24) {
      return currentTime >= todayHours.open || currentTime <= (todayHours.close - 24);
    }
    
    return currentTime >= todayHours.open && currentTime < todayHours.close;
  };

  const currentDay = getCurrentDay();
  const isOpen = isCurrentlyOpen();

  const getStatusMessage = () => {
    if (isClosedForGSPF()) {
      return {
        status: 'CLOSED',
        message: 'Currently CLOSED until Tuesday, May 19 for the Golden State Pinball Festival',
        isOpen: false
      };
    }
    const now = new Date();
    const dayOfWeek = now.getDay();
     
    // Special case: Sunday August 10th, 2025 opens at noon because Elsie forgot
    // If you inspect this, yes, Elsie forgot. Sorry Elsie!
    const isSpecialSunday =
      now.getFullYear() === 2025 &&
      now.getMonth() === 7 &&
      now.getDate() === 10 &&
      dayOfWeek === 0;

    const hours = {
      0: { open: isSpecialSunday ? 12 : 11, close: 24 }, // Sunday: 11am - Midnight (except Aug 10, 2025: Noon)
      1: { open: 11, close: 21 }, // Monday: 11am - 9pm
      2: { open: 11, close: 21 }, // Tuesday: 11am - 9pm
      3: { open: 12, close: 23 }, // Wednesday: Noon - 11pm
      4: { open: 12, close: 24 }, // Thursday: Noon - Midnight
      5: { open: 11, close: 25 }, // Friday: 11am - 1am (next day)
      6: { open: 11, close: 25 }  // Saturday: 11am - 1am (next day)
    };

    const formatTime = (hour) => {
      if (hour === 24) return '12:00am';
      if (hour > 24) return `${hour - 24}:00am`;
      if (hour === 12) return '12:00pm';
      if (hour > 12) return `${hour - 12}:00pm`;
      return `${hour}:00am`;
    };

    const todayHours = hours[dayOfWeek];
    
    if (isOpen) {
      const closeTime = formatTime(todayHours.close);
      return {
        status: 'OPEN',
        message: `Currently OPEN until ${closeTime}`,
        isOpen: true
      };
    } else {
      // If closed, find next opening time
      const currentHour = now.getHours();
      let nextOpenDay = dayOfWeek;
      let nextOpenHour = todayHours.open;
      
      // If we're before today's opening time, next opening is today
      if (currentHour < todayHours.open) {
        nextOpenDay = dayOfWeek;
        nextOpenHour = todayHours.open;
      }
      // If we're past today's closing time, next opening is tomorrow
      else if (currentHour >= todayHours.close || (todayHours.close > 24 && currentHour >= (todayHours.close - 24))) {
        nextOpenDay = (dayOfWeek + 1) % 7;
        nextOpenHour = hours[nextOpenDay].open;
      }
      
      const openTime = formatTime(nextOpenHour);
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = nextOpenDay === dayOfWeek ? 'today' : dayNames[nextOpenDay];
      
      return {
        status: 'CLOSED',
        message: `Currently CLOSED until ${openTime} ${dayName !== 'today' ? dayName : ''}`.trim(),
        isOpen: false
      };
    }
  };

  const statusInfo = getStatusMessage();

  return (
    <div style={styles.container}>
      <div style={styles.section}>
        {showGSPFAnnouncement() && (
          <div style={styles.announcement}>
            <h2 style={styles.announcementTitle}>
              Closed Wed May 13 – Mon May 18, 2026
            </h2>
            <p style={styles.announcementText}>
              We'll be at the Golden State Pinball Festival! Back open Tuesday, May 19. Come find us at the festival.
            </p>
            <a
              href="https://goldenstatepinball.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/images/gspf2026.jpg"
                alt="Golden State Pinball Festival 2026"
                style={styles.announcementImage}
              />
            </a>
          </div>
        )}
        <h1 style={styles.title}>Welcome to Capital Pinball Parlor!</h1>
        <p style={styles.statusMessage}>
          {/*
            If you inspect this element, Elsie forgot we open at noon on August 10, 2025.
            Next Sunday it's back to 11am. Sorry Elsie!
          */}
          Currently <span style={statusInfo.isOpen ? styles.openText : styles.closedText}>
            {statusInfo.status}
          </span> until {statusInfo.message.split('until ')[1]}
        </p>
        <p style={styles.notice}>
          Please check our{' '}
          <a 
            href="https://www.instagram.com/capitalpinballparlor" 
            target="_blank" 
            rel="noopener noreferrer"
            style={styles.link}
          >
            Instagram
          </a>{' '}
          for any temporary hour change announcements.
        </p>

        <div style={styles.discordRow}>
          <DiscordButton />
        </div>

        <div style={styles.hoursSection}>
          <h2 style={styles.header}>Hours:</h2>
          <p style={currentDay === 'Sunday' ? {...styles.hours, ...styles.currentDay} : styles.hours}>
            Sunday: 11am - Midnight
          </p>
          <p style={currentDay === 'Monday' ? {...styles.hours, ...styles.currentDay} : styles.hours}>
            Monday: 11am - 9pm
          </p>
          <p style={currentDay === 'Tuesday' ? {...styles.hours, ...styles.currentDay} : styles.hours}>
            Tuesday: 11am - 9pm
          </p>
          <p style={currentDay === 'Wednesday' ? {...styles.hours, ...styles.currentDay} : styles.hours}>
            Wednesday: Noon - 11pm
          </p>
          <p style={currentDay === 'Thursday' ? {...styles.hours, ...styles.currentDay} : styles.hours}>
            Thursday: Noon - Midnight
          </p>
          <p style={currentDay === 'Friday' ? {...styles.hours, ...styles.currentDay} : styles.hours}>
            Friday: 11am - 1am
          </p>
          <p style={currentDay === 'Saturday' ? {...styles.hours, ...styles.currentDay} : styles.hours}>
            Saturday: 11am - 1am
          </p>
        </div>

        <div style={styles.pricingSection}>
          <h2 style={styles.header}>Pricing:</h2>
          <div style={styles.pricingGrid}>
            <div style={styles.pricingColumn}>
              <div style={styles.pricingColumnHeader}>
                <span style={styles.weekdayHeaderText}>WEEKDAY RATES</span>
              </div>
              <div style={styles.pricingRow}>
                <span style={styles.price}>$5</span>
                <span style={styles.pricingLabel}>30 Minutes</span>
              </div>
              <div style={styles.pricingDivider} />
              <div style={styles.pricingRow}>
                <span style={styles.price}>$10</span>
                <span style={styles.pricingLabel}>Kids / Seniors Unlimited</span>
              </div>
              <div style={styles.pricingDivider} />
              <div style={styles.pricingRow}>
                <span style={styles.price}>$15</span>
                <span style={styles.pricingLabel}>Adults Unlimited</span>
              </div>
            </div>
            <div style={styles.pricingColumn}>
              <div style={{...styles.pricingColumnHeader, ...styles.weekendColumnHeader}}>
                <span style={styles.weekendHeaderText}>WEEKEND RATES</span>
              </div>
              <div style={styles.pricingRow}>
                <span style={styles.price}>$7</span>
                <span style={styles.pricingLabel}>30 Minutes</span>
              </div>
              <div style={styles.pricingDivider} />
              <div style={styles.pricingRow}>
                <span style={styles.price}>$15</span>
                <span style={styles.pricingLabel}>Kids / Seniors Unlimited</span>
              </div>
              <div style={styles.pricingDivider} />
              <div style={styles.pricingRow}>
                <span style={styles.price}>$20</span>
                <span style={styles.pricingLabel}>Adults Unlimited</span>
              </div>
            </div>
          </div>
          <p style={styles.tournamentNote}>Pinball Tournaments every Friday at 7:30PM</p>
          <p style={styles.pricing}>
            <a 
              href="/membership" 
              style={styles.membershipLink}
            >
              $80 - Monthly Membership
            </a>
          </p>
        </div>

        <p style={styles.description}>
          Capital Pinball Parlor is Sacramento's premier pinball arcade.
        </p>
        <p style={styles.description}>
          Your ultimate pinball experience starts here.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
    padding: '20px',
    paddingTop: '10px',
  },
  section: {
    background: 'var(--card-bg)',
    color: 'var(--text-primary)',
    padding: 'clamp(24px, 5vw, 44px) clamp(18px, 4.5vw, 40px)',
    textAlign: 'center',
    maxWidth: '620px',
    width: '100%',
    borderRadius: '14px',
    border: '1px solid var(--card-border)',
    boxShadow: 'var(--card-shadow)',
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 'clamp(1.45rem, 5.2vw, 1.9rem)',
    marginBottom: '20px',
    color: 'var(--accent-gold)',
    textShadow: 'var(--accent-gold-glow)',
    lineHeight: 1.25,
  },
  statusMessage: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: 'clamp(1.05rem, 3.6vw, 1.3rem)',
    fontWeight: '600',
    marginBottom: '24px',
    color: 'var(--text-primary)',
    letterSpacing: '0.5px',
  },
  openText: {
    color: '#00ff88',
    fontWeight: 'bold',
    textShadow: '0 0 10px rgba(0, 255, 136, 0.35)',
  },
  closedText: {
    color: '#ff3366',
    fontWeight: 'bold',
    textShadow: '0 0 10px rgba(255, 51, 102, 0.35)',
  },
  subtitle: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '20px',
    fontWeight: 'normal',
    marginBottom: '30px',
    color: 'var(--text-secondary)',
  },
  header: {
    fontFamily: 'var(--font-heading)',
    fontSize: 'clamp(1.05rem, 3.2vw, 1.25rem)',
    marginBottom: '14px',
    color: 'var(--accent-cyan)',
    textShadow: 'var(--accent-cyan-glow)',
    letterSpacing: '1px',
  },
  discordRow: {
    marginBottom: '26px',
  },
  hoursSection: {
    marginBottom: '30px',
    padding: '18px 20px',
    background: 'var(--subpanel-bg)',
    borderRadius: '10px',
    border: '1px solid var(--subpanel-border)',
  },
  hours: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '17px',
    fontWeight: 500,
    marginBottom: '6px',
    color: 'var(--text-secondary)',
  },
  currentDay: {
    color: 'var(--accent-gold)',
    fontWeight: 'bold',
    background: 'var(--currentday-bg)',
    border: '1px solid var(--currentday-border)',
    boxShadow: 'var(--currentday-shadow)',
    padding: '5px 14px',
    borderRadius: '6px',
    textShadow: 'var(--currentday-text-glow)',
  },
  notice: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '13px',
    marginTop: '14px',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
  link: {
    color: 'var(--accent-pink)',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  membershipLink: {
    color: 'var(--accent-pink)',
    textDecoration: 'none',
    fontSize: '18px',
    fontWeight: 700,
  },
  pricingSection: {
    marginBottom: '30px',
    padding: '18px 20px',
    background: 'var(--subpanel-bg)',
    borderRadius: '10px',
    border: '1px solid var(--subpanel-border)',
  },
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',
    marginBottom: '20px',
  },
  pricingColumn: {
    background: 'var(--pricecol-bg)',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid var(--pricecol-border)',
  },
  pricingColumnHeader: {
    background: 'var(--pricecol-header-weekday)',
    padding: '10px 8px',
    textAlign: 'center',
    borderBottom: '2px solid var(--pricecol-header-weekday-accent)',
  },
  weekendColumnHeader: {
    background: 'var(--pricecol-header-weekend)',
    borderBottom: '2px solid var(--pricecol-header-weekend-accent)',
  },
  weekdayHeaderText: {
    color: 'var(--accent-cyan)',
    fontFamily: 'var(--font-heading)',
    fontSize: '11px',
    letterSpacing: '1px',
  },
  weekendHeaderText: {
    color: 'var(--accent-pink)',
    fontFamily: 'var(--font-heading)',
    fontSize: '11px',
    letterSpacing: '1px',
  },
  pricingRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    gap: '8px',
  },
  price: {
    color: 'var(--accent-gold)',
    fontFamily: 'var(--font-heading)',
    fontSize: '18px',
    minWidth: '36px',
    textAlign: 'right',
  },
  pricingLabel: {
    color: 'var(--text-secondary)',
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '13px',
    fontWeight: 600,
    textAlign: 'left',
    lineHeight: '1.3',
  },
  pricingDivider: {
    height: '1px',
    backgroundColor: 'var(--pricecol-divider)',
    margin: '0 12px',
  },
  tournamentNote: {
    color: 'var(--accent-pink)',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: '700',
    fontSize: '15px',
    marginBottom: '14px',
    marginTop: '4px',
  },
  pricing: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
    color: 'var(--text-secondary)',
  },
  description: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '16px',
    fontWeight: '500',
    marginTop: '20px',
    lineHeight: '1.6',
    color: 'var(--text-muted)',
  },
  announcement: {
    background: 'var(--subpanel-bg)',
    border: '2px solid var(--accent-gold)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    boxShadow: 'var(--accent-gold-glow)',
  },
  announcementTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: '20px',
    color: 'var(--accent-gold)',
    textShadow: 'var(--accent-gold-glow)',
    marginBottom: '10px',
    lineHeight: 1.25,
  },
  announcementText: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '16px',
    lineHeight: 1.5,
  },
  announcementImage: {
    display: 'block',
    width: '100%',
    maxWidth: '500px',
    height: 'auto',
    margin: '0 auto',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

export default Home;
